// apps/web/src/components/CreateCustomerForm.tsx
import { useState } from 'react';
import type { Channel, StudioId } from '@/domain/types';
import { matchCustomer, createCustomer } from '@/domain/selfBookFlow';
import { normalizeInstagramHandle, isValidInstagramHandle } from '@/domain/instagram';
import { useBrainStore } from '@/state/brainStore';
import { customerName } from '@/utils/format';
import { AlertTriangle } from 'lucide-react';

interface Props {
  studioId: StudioId;
  sourceChannel?: Channel;
  defaultName?: string;
  onResolved: (customerId: string) => void;
  onCancel: () => void;
}

const MATCH_FIELD = { phone: 'number', email: 'email', instagram: 'Instagram handle' } as const;

const input = 'w-full bg-ink-deep border border-gold-dim/40 rounded-md px-3 py-2 text-sm text-cream focus:border-gold-bright focus:outline-none';

export default function CreateCustomerForm({ studioId, sourceChannel, defaultName, onResolved, onCancel }: Props) {
  const customers = useBrainStore((s) => s.customers);
  const [firstName, setFirstName] = useState(defaultName ?? '');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [handle, setHandle] = useState('');
  const [error, setError] = useState('');
  const [hintIds, setHintIds] = useState<string[]>([]);
  // tv-2mwa: a phone / email / Instagram already on file never switches the
  // caller to that customer silently. Staff see whose it is and choose.
  const [match, setMatch] = useState<{ id: string; via: 'phone' | 'email' | 'instagram' } | null>(null);

  const hasStrongId = phone.trim() || email.trim() || normalizeInstagramHandle(handle);
  const handleNorm = normalizeInstagramHandle(handle);

  const submit = () => {
    if (!firstName.trim() || !hasStrongId) {
      setError('First name and at least one of phone, email, or Instagram is required.');
      return;
    }
    if (handle.trim() && !isValidInstagramHandle(handleNorm)) {
      setError('That Instagram handle looks invalid.');
      return;
    }
    const name = `${firstName.trim()} ${lastName.trim()}`.trim();
    const m = matchCustomer({ mobile: phone, email, instagramHandle: handle, name });
    if (m.kind === 'matched') {
      setMatch({ id: m.id, via: m.via });
      return;
    }
    if (m.nameHints.length && !hintIds.length) { setHintIds(m.nameHints); return; } // show soft hint once
    onResolved(createCustomer({ name, mobile: phone, email, instagramHandle: handle, studioId, sourceChannel }));
  };

  return (
    <div className="space-y-3" data-testid="create-customer-form">
      <div className="grid grid-cols-2 gap-3">
        <label className="block"><span className="label-small-caps">First name *</span>
          <input data-testid="ccf-firstName" className={input} value={firstName} onChange={(e) => setFirstName(e.target.value)} /></label>
        <label className="block"><span className="label-small-caps">Last name</span>
          <input data-testid="ccf-lastName" className={input} value={lastName} onChange={(e) => setLastName(e.target.value)} /></label>
      </div>
      <label className="block"><span className="label-small-caps">Phone</span>
        <input data-testid="ccf-phone" className={input} value={phone} onChange={(e) => { setPhone(e.target.value); setMatch(null); }} /></label>
      <div className="grid grid-cols-2 gap-3">
        <label className="block"><span className="label-small-caps">Email</span>
          <input data-testid="ccf-email" className={input} value={email} onChange={(e) => { setEmail(e.target.value); setMatch(null); }} /></label>
        <label className="block"><span className="label-small-caps">Instagram</span>
          <input data-testid="ccf-handle" className={input} value={handle} placeholder="@handle" onChange={(e) => { setHandle(e.target.value); setMatch(null); }} /></label>
      </div>
      <p className="font-inter text-[11px] text-cream-faint">First name + at least one of phone, email, or Instagram.</p>
      {match && customers[match.id] ? (
        <div
          role="status"
          className="flex gap-3 items-start rounded-lg border border-orange/45 bg-orange/10 px-4 py-3"
          data-testid="ccf-match"
        >
          <AlertTriangle size={18} className="text-orange shrink-0 mt-0.5" aria-hidden="true" />
          <div className="flex flex-col gap-2 font-inter text-[13px] text-cream">
            <span>
              <strong>
                This {MATCH_FIELD[match.via]} belongs to {customerName(customers[match.id]!)}
              </strong>
              {customers[match.id]!.suburb ? `, ${customers[match.id]!.suburb}` : ''}.
            </span>
            <div className="flex gap-2">
              <button type="button" data-testid="ccf-match-use" onClick={() => onResolved(match.id)} className="btn-primary text-xs">
                Use {customerName(customers[match.id]!)}
              </button>
              <button type="button" data-testid="ccf-match-back" onClick={() => setMatch(null)} className="btn-ghost text-xs">
                Go back
              </button>
            </div>
          </div>
        </div>
      ) : null}
      {hintIds.length ? (
        <div className="panel p-3" data-testid="ccf-namehint">
          <p className="label-small-caps mb-2">Existing customers named “{`${firstName} ${lastName}`.trim()}”</p>
          {hintIds.map((id) => (
            <button key={id} type="button" data-testid={`ccf-use-${id}`} onClick={() => onResolved(id)}
              className="block w-full text-left text-sm text-cream hover:text-gold-bright py-1">
              Use {customerName(customers[id]!)}
            </button>
          ))}
        </div>
      ) : null}
      {error ? <p className="font-inter text-[11px] text-blood" data-testid="ccf-error">{error}</p> : null}
      <div className="flex gap-2">
        <button type="button" data-testid="ccf-submit" onClick={submit} className="btn-primary text-sm">
          {hintIds.length ? 'Create new anyway' : 'Create & select'}
        </button>
        <button type="button" data-testid="ccf-cancel" onClick={onCancel} className="btn-ghost text-sm">Cancel</button>
      </div>
    </div>
  );
}
